import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import MilitaryTechRoundedIcon from '@mui/icons-material/MilitaryTechRounded';
import { toNumber, formatInt } from '../../utils/format';
import WidgetShell from './WidgetShell';

function RecordTile({ icon, iconColor, label, value, subvalue }) {
  return (
    <Box
      sx={{
        p: 1.3,
        borderRadius: 2,
        backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.05),
        border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.14)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.2 }}>
        <Box sx={{ color: iconColor, fontSize: 14, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            fontSize: 9.5,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontWeight: 900, fontSize: 17, lineHeight: 1.15, letterSpacing: -0.3 }}>
        {value}
      </Typography>
      {subvalue && (
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
          {subvalue}
        </Typography>
      )}
    </Box>
  );
}

function PersonalRecordsWidget({ stats }) {
  return (
    <WidgetShell
      icon={<EmojiEventsRoundedIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
      title="Personal Records"
      cardSx={{
        border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        boxShadow: (theme) => `0 12px 28px ${alpha(theme.palette.warning.main, 0.1)}`,
        background: (theme) =>
          `linear-gradient(160deg, ${alpha(theme.palette.warning.light, 0.1)} 0%, ${alpha(
            theme.palette.background.paper,
            0.97
          )} 55%)`,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
        }}
      >
        <RecordTile
          icon={<EmojiEventsRoundedIcon fontSize="inherit" />}
          iconColor="warning.main"
          label="Best Word"
          value={stats?.scoring?.bestWord?.word || '--'}
          subvalue={`${formatInt(stats?.scoring?.bestWord?.score)} pts`}
        />
        <RecordTile
          icon={<FlagRoundedIcon fontSize="inherit" />}
          iconColor="primary.main"
          label="Longest Word"
          value={stats?.words?.longestWord?.word || '--'}
          subvalue={`${formatInt(stats?.words?.longestWord?.length)} letters`}
        />
        <RecordTile
          icon={<AutoGraphRoundedIcon fontSize="inherit" />}
          iconColor="success.main"
          label="Best Submit"
          value={`${formatInt(stats?.scoring?.bestSubmit?.score)} pts`}
          subvalue={`${formatInt(stats?.scoring?.bestSubmit?.wordCount)} words scored`}
        />
        <RecordTile
          icon={<TrendingUpRoundedIcon fontSize="inherit" />}
          iconColor="info.main"
          label="Best Run Score"
          value={`${formatInt(stats?.runs?.highestScoreReached)} pts`}
        />
        <RecordTile
          icon={<LayersRoundedIcon fontSize="inherit" />}
          iconColor="secondary.main"
          label="Highest Round"
          value={
            toNumber(stats?.runs?.highestRoundReached) > 0
              ? `Round ${formatInt(stats?.runs?.highestRoundReached)}`
              : '--'
          }
        />
        <RecordTile
          icon={<MilitaryTechRoundedIcon fontSize="inherit" />}
          iconColor="warning.main"
          label="Total Wins"
          value={formatInt(stats?.runs?.won)}
        />
      </Box>
    </WidgetShell>
  );
}

export default PersonalRecordsWidget;
